import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import UnitControl from './components/UnitControl';
import Finance from './components/Finance';
import FinanceTransactions from './components/FinanceTransactions';
import FinanceSubscriptionDetail from './components/FinanceSubscriptionDetail';
import StewardManagement from './components/StewardManagementTable';
import StewardDetail from './components/StewardDetail';
import RPManagement from './components/RPManagementTable';
import RPDetail from './components/RPDetail';
import PUSKUDManagement from './components/PUSKUDManagement';
import AuditLogs from './components/AuditLogs';
import SystemSettings from './components/SystemSettings';
import CardManagement from './components/CardManagement';
import UserManagement from './components/UserManagement';
import AdminAccountManagement from './components/AdminAccountManagement';
import UnitDetail from './components/UnitDetail';
import ApplicationReview from './components/ApplicationReview';
import ProfitSharingFinance from './components/ProfitSharingFinance';
import FirmwareUpgrade from './components/FirmwareUpgrade';
import RuntimeLogs from './components/RuntimeLogs';
import AppContentManagement from './components/AppContentManagement';
import LoginPage from './pages/LoginPage';
import DeviceAlertsMonitor from './components/DeviceAlertsMonitor';
import NotificationCenter from './components/NotificationCenter';
import StewardCheckinRecords from './components/StewardCheckinRecords';
import ProfitRuleConfig from './components/ProfitRuleConfig';
import websocketService from './services/websocket';
import { useAuthStore } from './store/authStore';
import useAdminTheme from './hooks/useAdminTheme';
import PermissionRoute from './components/PermissionRoute';
import BankAccountManagement from './components/BankAccountManagement';
import GrowthProgramSettings from './components/GrowthProgramSettings';
import SimCardManagement from './components/SimCardManagement';
import StewardChangeManagement from './components/StewardChangeManagement';

function App() {
  const token = useAuthStore((state) => state.token);
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const refreshAdminSession = useAuthStore((state) => state.refreshAdminSession);
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  const authError = useAuthStore((state) => state.error);
  const { theme } = useAdminTheme();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!token) return undefined;
    refreshAdminSession().catch(() => {});
    return undefined;
  }, [refreshAdminSession, token]);

  useEffect(() => {
    if (!token) {
      websocketService.disconnect();
      return undefined;
    }

    websocketService.connect(token);
    return () => websocketService.disconnect();
  }, [token]);

  return (
    <Router>
      <div className={`${theme === 'dark' ? 'admin-dark-theme' : 'admin-light-theme'} admin-shell flex min-h-screen flex-col overflow-hidden md:flex-row`}>
        {token && !isAuthLoading && !authError && <Navigation />}
        <main className="admin-shell relative h-auto min-h-screen flex-1 overflow-y-auto md:h-screen">
          <div className="relative z-10">
            <Routes>
              <Route path="/login" element={!token ? <LoginPage /> : <Navigate to="/" replace />} />

              <Route path="/" element={<PermissionRoute permission="view_dashboard"><Dashboard /></PermissionRoute>} />
              <Route path="/units" element={<PermissionRoute permission="view_units"><UnitControl /></PermissionRoute>} />
              <Route path="/units/:id" element={<PermissionRoute permission="view_units"><UnitDetail /></PermissionRoute>} />
              <Route path="/sim-cards" element={<PermissionRoute permission="view_sim_cards"><SimCardManagement /></PermissionRoute>} />
              <Route path="/steward-changes" element={<PermissionRoute permission="review_steward_changes"><StewardChangeManagement /></PermissionRoute>} />
              <Route path="/users" element={<PermissionRoute permission="view_users_roles"><UserManagement /></PermissionRoute>} />
              <Route path="/admin-accounts" element={<PermissionRoute permission="manage_admin_accounts"><AdminAccountManagement /></PermissionRoute>} />
              <Route path="/stewards" element={<PermissionRoute permission="view_partner_network"><StewardManagement /></PermissionRoute>} />
              <Route path="/stewards/:id" element={<PermissionRoute permission="view_partner_network"><StewardDetail /></PermissionRoute>} />
              <Route path="/kuds" element={<PermissionRoute permission="view_partner_network"><RPManagement /></PermissionRoute>} />
              <Route path="/kuds/:id" element={<PermissionRoute permission="view_partner_network"><RPDetail /></PermissionRoute>} />
              <Route path="/puskud" element={<PermissionRoute permission="view_partner_network"><PUSKUDManagement /></PermissionRoute>} />
              <Route path="/rps" element={<PermissionRoute permission="view_partner_network"><Navigate to="/kuds" replace /></PermissionRoute>} />
              <Route path="/rps/:id" element={<PermissionRoute permission="view_partner_network"><RPDetail /></PermissionRoute>} />
              <Route path="/finance" element={<PermissionRoute permission="view_finance"><Finance /></PermissionRoute>} />
              <Route path="/finance/subscriptions" element={<PermissionRoute permission="view_finance"><FinanceSubscriptionDetail /></PermissionRoute>} />
              <Route path="/transactions" element={<PermissionRoute permission="view_finance"><FinanceTransactions /></PermissionRoute>} />
              <Route path="/withdrawals" element={<PermissionRoute permission="view_finance"><Navigate to="/profit-sharing-finance" replace /></PermissionRoute>} />
              <Route path="/partners" element={<PermissionRoute permission="view_partner_network"><Navigate to="/kuds" replace /></PermissionRoute>} />
              <Route path="/applications" element={<PermissionRoute permission="review_applications"><ApplicationReview /></PermissionRoute>} />
              <Route path="/partners/:id" element={<PermissionRoute permission="view_partner_network"><RPDetail /></PermissionRoute>} />
              <Route path="/cards" element={<PermissionRoute permission="view_physical_cards"><CardManagement /></PermissionRoute>} />
              <Route path="/audit" element={<PermissionRoute permission="view_audit_logs"><AuditLogs /></PermissionRoute>} />
              <Route path="/settings" element={<PermissionRoute permission="manage_business_settings"><SystemSettings /></PermissionRoute>} />
              <Route path="/settings/growth" element={<PermissionRoute permission="manage_business_settings"><GrowthProgramSettings /></PermissionRoute>} />
              <Route path="/profit-sharing-finance" element={<PermissionRoute permission="view_finance"><ProfitSharingFinance /></PermissionRoute>} />
              <Route path="/bank-accounts" element={<PermissionRoute permission="view_bank_accounts"><BankAccountManagement /></PermissionRoute>} />
              <Route path="/device-bindings" element={<PermissionRoute permission="view_units"><Navigate to="/units" replace /></PermissionRoute>} />
              <Route path="/device-alerts" element={<PermissionRoute permission="view_device_alerts"><DeviceAlertsMonitor /></PermissionRoute>} />
              <Route path="/notifications" element={<PermissionRoute permission="view_device_alerts"><NotificationCenter /></PermissionRoute>} />
              <Route path="/steward-checkins" element={<PermissionRoute permission="review_steward_checkins"><StewardCheckinRecords /></PermissionRoute>} />
              <Route path="/profit-rules" element={<PermissionRoute permission="manage_profit_rules"><ProfitRuleConfig /></PermissionRoute>} />
              <Route path="/firmware" element={<PermissionRoute permission="manage_firmware"><Navigate to="/firmware/upgrade" replace /></PermissionRoute>} />
              <Route path="/firmware/upgrade" element={<PermissionRoute permission="manage_firmware"><FirmwareUpgrade defaultTab="deploy" /></PermissionRoute>} />
              <Route path="/firmware/upload" element={<PermissionRoute permission="manage_firmware"><FirmwareUpgrade defaultTab="upload" /></PermissionRoute>} />
              <Route path="/firmware/list" element={<PermissionRoute permission="manage_firmware"><FirmwareUpgrade defaultTab="deploy" /></PermissionRoute>} />
              <Route path="/firmware/monitor" element={<PermissionRoute permission="manage_firmware"><FirmwareUpgrade defaultTab="monitor" /></PermissionRoute>} />
              <Route path="/runtime-logs" element={<PermissionRoute permission="view_runtime_logs"><RuntimeLogs /></PermissionRoute>} />
              <Route path="/content" element={<PermissionRoute permission="manage_content"><AppContentManagement /></PermissionRoute>} />
              <Route path="*" element={<Navigate to={token ? '/' : '/login'} replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </Router>
  );
}

export default App;
